import { type Dtos, formatToDollar } from "@inrev/common";
import { DateTime } from "luxon";

type FactorValue = Dtos.Decisioning.FactorValue;
type FactorValueType = Dtos.Decisioning.FactorValueType;

/** What the value input holds while the underwriter types: text for most types, a choice for booleans. */
export type ObservedValueDraft = string | boolean | null;

const NUMERIC_TYPES: readonly FactorValueType[] = ["number", "currency", "percent", "ratio"];
const DRAFT_NOISE = /[$,%x\s]/g;

export const formatObservedValue = (value: FactorValue, valueType: FactorValueType): string => {
	if (typeof value === "boolean") return value ? "Yes" : "No";
	if (typeof value === "string") {
		return valueType === "date" ? DateTime.fromISO(value).toFormat("MM/dd/yyyy") : value;
	}
	switch (valueType) {
		case "currency":
			return `${formatToDollar(value)}`;
		case "percent":
			return `${value}%`;
		case "ratio":
			return `${value}x`;
		default:
			return value.toLocaleString();
	}
};

export const toObservedValueDraft = (value: FactorValue | null): ObservedValueDraft =>
	typeof value === "number" ? String(value) : value;

/** The value to send, or `undefined` while the draft is empty or does not fit the type. */
export const parseObservedValueDraft = (
	draft: ObservedValueDraft,
	valueType: FactorValueType,
): FactorValue | undefined => {
	if (draft === null) return undefined;
	if (valueType === "boolean") return typeof draft === "boolean" ? draft : undefined;
	if (typeof draft !== "string") return undefined;
	const trimmed = draft.trim();
	if (trimmed === "") return undefined;
	if (NUMERIC_TYPES.includes(valueType)) {
		const parsed = Number(trimmed.replace(DRAFT_NOISE, ""));
		return Number.isFinite(parsed) ? parsed : undefined;
	}
	return trimmed;
};

/**
 * A typed number as it reads once the field is left: `1234567` becomes `1,234,567.00` for
 * currency and `1,234,567` otherwise. Anything that is not a number is left as typed.
 */
export const formatObservedValueDraft = (draft: string, valueType: FactorValueType): string => {
	if (!NUMERIC_TYPES.includes(valueType)) return draft;
	const value = parseObservedValueDraft(draft, valueType);
	if (typeof value !== "number") return draft;
	const fractionDigits = valueType === "currency" ? 2 : 0;
	return value.toLocaleString("en-US", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: valueType === "currency" ? 2 : 4,
	});
};

/** The draft as typed while editing, without the grouping added on blur. */
export const unformatObservedValueDraft = (draft: string, valueType: FactorValueType): string =>
	NUMERIC_TYPES.includes(valueType) ? draft.replace(DRAFT_NOISE, "") : draft;
